import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  SectionKeyType,
  SectionStatus,
  SectionStatusType,
  SUMMARY_SECTIONS
} from '../data-qa-constants';
import { ConfigSummary } from '../data-qa-types';
import { modelLabelForConfig } from '../data-qa-utils';
import { loadConfig } from '../api/data-qa-api';

// One model's session-tracked review state, mirrored from the reducer
// whenever it's the active model — see DataQA.tsx's sessionReviewByConfig.
export interface SessionModelReview {
  sectionStatus: Record<SectionKeyType, SectionStatusType>;
  sectionNotes: Record<SectionKeyType, string>;
  // Sections whose status/notes were themselves auto-inherited from a THIRD model
  // this session, not a genuine decision made on this one -- see DataQaState's own
  // field of the same name. Never treated as a valid source below.
  inheritedSections: Partial<Record<SectionKeyType, boolean>>;
}

export interface InheritedSectionReview {
  // A model-type label (e.g. "Commerce MMM"), NOT clientName — clientName is
  // identical across every model for the same company, so it can't actually
  // tell the reviewer which OTHER model this came from.
  sourceModelLabel: string;
  status: SectionStatusType;
  notes: string;
}

// For each of the active model's sections still worth checking, look across
// every OTHER discovered model's cached load result (warmed by
// usePrefetchOtherModels) for one whose section produced the identical
// content hash AND already has a GENUINE decided (non-unreviewed, non-inherited)
// status this session -- see inheritedSections. Two configs sharing a section's
// hash have byte-identical
// underlying data (see computeSectionContentHashes, backend), so reviewing it
// once should count everywhere it matches — this is what lets DTC and
// Commerce share one Channels review instead of two.
export const useSharedSectionReview = (
  cid: string | undefined,
  configs: ConfigSummary[],
  activeConfigPath: string | undefined,
  activeSectionContentHash: Record<string, string> | undefined,
  sessionReviewByConfig: Record<string, SessionModelReview>,
  // Gates this hook's useQueries the same way usePrefetchOtherModels gates its own
  // sequential prefetch (on activeLoadResult) -- see useSectionDataInvariance's own
  // doc comment for why: without this, useQueries fires N concurrent loadConfig
  // fetches for every sibling model as soon as configs/activeConfigPath are set,
  // well before the active model itself has loaded.
  activeModelLoaded: boolean
): Partial<Record<SectionKeyType, InheritedSectionReview>> => {
  // Same otherConfigs/queries/useQueries shape as useSectionDataInvariance -- this
  // hook used to read queryClient.getQueryData(...) imperatively inside useMemo
  // instead, which isn't reactive: queryClient itself is a stable reference across
  // renders, so a sibling model's prefetch settling and landing in the cache doesn't
  // by itself re-trigger this memo (only a change to one of the OTHER deps does),
  // meaning an inherited status/notes could sit un-surfaced until some unrelated
  // state change happened to re-run it. useQueries subscribes to the SAME
  // ['mmmDataQALoad', cid, config.configPath] keys usePrefetchOtherModels/
  // useSectionDataInvariance already observe -- React Query dedupes concurrent
  // observers of the same key, so this doesn't trigger a duplicate fetch.
  const otherConfigs = useMemo(() => {
    if (!cid || !activeConfigPath) return [];
    return configs.filter(config => config.configPath !== activeConfigPath);
  }, [cid, activeConfigPath, configs]);

  const queries = useMemo(
    () =>
      otherConfigs.map(config => ({
        queryKey: ['mmmDataQALoad', cid, config.configPath],
        queryFn: () => loadConfig(cid ?? '', config.configPath),
        staleTime: Infinity,
        enabled: activeModelLoaded
      })),
    [otherConfigs, cid, activeModelLoaded]
  );

  const results = useQueries({ queries });

  return useMemo(() => {
    const result: Partial<Record<SectionKeyType, InheritedSectionReview>> = {};
    if (!cid || !activeConfigPath || !activeSectionContentHash) return result;

    otherConfigs.forEach((config, i) => {
      const otherReview = sessionReviewByConfig[config.configPath];
      if (!otherReview) return;
      const queryResult = results[i];
      if (queryResult?.status !== 'success') return;
      const otherHashes = queryResult.data?.dataset.sectionContentHash;
      if (!otherHashes) return;

      for (const sectionKey of SUMMARY_SECTIONS) {
        if (result[sectionKey]) continue; // already matched from an earlier model
        // An inherited copy is never a valid source -- only a genuine decision can be
        // attributed to. Without this, once two models both carry the same inherited
        // status, whichever happens to come first in array order wins, producing
        // contradictory attribution (confirmed live 2026-08-27, see inheritedSections).
        if (otherReview.inheritedSections[sectionKey]) continue;
        const status = otherReview.sectionStatus[sectionKey];
        if (!status || status === SectionStatus.UNREVIEWED) continue;
        const hash = activeSectionContentHash[sectionKey];
        if (hash && hash === otherHashes[sectionKey]) {
          result[sectionKey] = {
            sourceModelLabel: modelLabelForConfig(configs, config.configPath),
            status,
            notes: otherReview.sectionNotes[sectionKey] ?? ''
          };
        }
      }
    });
    return result;
  }, [
    cid,
    activeConfigPath,
    activeSectionContentHash,
    configs,
    otherConfigs,
    sessionReviewByConfig,
    results
  ]);
};
